import React from "react";
import { Button, Card, Table, Tabs } from "antd";
import { ColumnType, TableProps } from "antd/lib/table";
import { isEmpty, isNil, map } from "lodash";
import { DropdownInput } from "components";
import useScrollToTop from "hooks/useScrollToTop";
import { AICC_LANGUAGE_STATUS_COLOR, AICC_LANGUAGE_STATUS_LABEL, AiccLanguageStatus } from "constants/aiccLanguage";
import styles from "./LearningAssets.module.scss";
import { confirmDiscardLearningAssetEdit } from "./confirmations";

interface FieldGuideItem {
  label: string;
  description: string;
}

interface LearningAssetVersionsState<T> {
  languageId?: number;
  setLanguageId: (languageId: number) => void;
  languageOptions: { label: string; value: number }[];
  versions: { status: AiccLanguageStatus }[];
  items: T[];
  groupStatus?: AiccLanguageStatus;
  setActiveStatus: (activeStatus: AiccLanguageStatus) => void;
  isEditable: boolean;
  isLoading: boolean;
}

interface Props<T> {
  title: string;
  headerExtra?: React.ReactNode;
  fieldGuideIntro?: React.ReactNode;
  fieldGuide?: FieldGuideItem[];
  addText?: string;
  columns: ColumnType<T>[];
  state: LearningAssetVersionsState<T>;
  pagination?: TableProps<T>["pagination"];
  onRow?: TableProps<T>["onRow"];
  rowClassName?: TableProps<T>["rowClassName"];
  isEditing?: boolean;
  isLocked?: boolean;
  onAdd?: () => void;
  filters?: React.ReactNode;
  toolbarActions?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
}

function LearningAssetPage<T extends { id: string }>({
  title,
  headerExtra,
  fieldGuideIntro,
  fieldGuide,
  addText,
  columns,
  state,
  pagination = false,
  onRow,
  rowClassName,
  isEditing = false,
  isLocked = false,
  onAdd,
  filters,
  toolbarActions,
  actions,
  children,
}: Props<T>) {
  const {
    languageId,
    setLanguageId,
    languageOptions,
    versions,
    items,
    groupStatus,
    setActiveStatus,
    isEditable,
    isLoading,
  } = state;

  useScrollToTop();

  const changeStatus = (key: string) => {
    const status = key as AiccLanguageStatus;
    if (isLocked) {
      return;
    }
    if (!isEditing) {
      setActiveStatus(status);
      return;
    }
    confirmDiscardLearningAssetEdit().then((confirmed) => confirmed && setActiveStatus(status));
  };

  return (
    <Card title={title} extra={headerExtra} className="page-container">
      {(fieldGuideIntro || !isEmpty(fieldGuide)) && (
        <div className={styles.fieldGuide}>
          {fieldGuideIntro && <div className={styles.fieldGuideIntro}>{fieldGuideIntro}</div>}
          {map(fieldGuide, ({ label, description }) => (
            <div key={label} className={styles.fieldGuideItem}>
              <span className={styles.fieldGuideLabel}>{`${label}: `}</span>
              <span>{description}</span>
            </div>
          ))}
        </div>
      )}
      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <div className={styles.filter}>
            <span className={styles.filterLabel}>Languages:</span>
            <div className={styles.filterSelect}>
              <DropdownInput
                options={languageOptions}
                value={languageId}
                onChange={setLanguageId}
                placeholder="Language"
                mode="single"
                disabled={isLoading || isEditing || isLocked}
              />
            </div>
          </div>
          {filters}
        </div>
        {isEditable && onAdd && (
          <Button type="primary" shape="round" disabled={isNil(languageId)} onClick={onAdd}>
            {addText}
          </Button>
        )}
        {toolbarActions}
      </div>
      <Tabs
        className={styles.statusTabs}
        style={
          {
            "--status-tab-active-color": groupStatus ? AICC_LANGUAGE_STATUS_COLOR[groupStatus] : undefined,
          } as React.CSSProperties
        }
        activeKey={groupStatus}
        onChange={changeStatus}
      >
        {map(versions, ({ status }) => (
          <Tabs.TabPane
            key={status}
            disabled={isLocked}
            tab={
              <span
                className={styles.statusTabLabel}
                style={{ "--status-tab-color": AICC_LANGUAGE_STATUS_COLOR[status] } as React.CSSProperties}
              >
                {AICC_LANGUAGE_STATUS_LABEL[status]}
              </span>
            }
          />
        ))}
      </Tabs>
      {actions && <div className={styles.actions}>{actions}</div>}
      <Table<T>
        className={styles.table}
        scroll={isEmpty(items) ? undefined : { x: "100%" }}
        loading={isLoading}
        dataSource={items}
        columns={columns}
        rowKey={(record) => `${groupStatus}-${record.id}`}
        pagination={pagination}
        onRow={onRow}
        rowClassName={rowClassName}
      />
      {children}
    </Card>
  );
}

export default LearningAssetPage;
